import { Button } from '../common/Button';
import { useSyncPaperclipRobotToken } from '../../hooks/useBotAdmin';
import { formatDisplayDateTime } from '../../utils/format';
import type {
  PaperclipRobotTokenSync,
  PaperclipRobotTokenSyncCompany,
} from '../../services/api/botAdmin';

/**
 * MKS-275 — the TopMonks Robot's token on the Paperclip tab: one row per bound
 * Factory™ company, with the same states as the per-person sync, and one
 * "Sync now" button.
 *
 * ONE button, not one per row. The Robot's token is a single credential every
 * company's agents share, so a per-company button would let an admin leave the
 * companies holding different tokens — a state nobody wants and nothing else in
 * this feature can produce.
 *
 * Nothing here is a secret: the card shows a status, a version, an error code and
 * timestamps. The token itself is never in the API response that feeds it.
 */
const BADGE_CLASSES: Record<string, string> = {
  synced: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
  drift: 'bg-amber-100 text-amber-700',
};

export function robotRowLabel(row: PaperclipRobotTokenSyncCompany): string {
  let base: string;
  switch (row.status) {
    case 'synced':
      base = row.version !== null ? `Synced (v${row.version})` : 'Synced';
      break;
    case 'failed':
      base = row.errorCode ? `Failed (${row.errorCode})` : 'Failed';
      break;
    case 'drift':
      base = 'Drift';
      break;
    default:
      base = 'Not synced';
  }
  return row.pendingOperation ? `${base} · ${row.pendingOperation} pending` : base;
}

function RobotRowBadge({ row }: { row: PaperclipRobotTokenSyncCompany }) {
  const classes = (row.status && BADGE_CLASSES[row.status]) || 'bg-gray-100 text-gray-600';
  return (
    <span
      title={`Last synced ${formatDisplayDateTime(row.syncedAt)}`}
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${classes}`}
    >
      {robotRowLabel(row)}
    </span>
  );
}

export function RobotTokenSyncCard({ robotTokenSync }: { robotTokenSync: PaperclipRobotTokenSync }) {
  const syncMutation = useSyncPaperclipRobotToken();

  // An environment whose task definition names no company cannot write at all, so
  // there is nothing to show rather than an empty, misleading card.
  if (!robotTokenSync.available) return null;

  const { switchOn, tokenPresent, companies } = robotTokenSync;
  const canSync = switchOn && tokenPresent;

  return (
    <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-gray-900">Robot token → Factory™ companies</p>
          <p className="text-sm mt-0.5">
            <span
              className={switchOn ? 'text-green-600 font-medium' : 'text-gray-600 font-medium'}
            >
              {switchOn ? 'Robot token sync: ON' : 'Robot token sync: OFF'}
            </span>
          </p>
          <p className="text-xs text-gray-500 mt-1">
            When ON, the TopMonks Robot Claude token set on the Channels tab is copied into each
            company&apos;s <code className="font-mono">ROBOT_CLAUDE_TOKEN</code> secret, which the
            shared Factory&trade; agents run on. Monksheets only ever rotates that one secret — it
            can neither create nor delete a company secret.
          </p>
          {!tokenPresent && (
            <p className="text-xs text-amber-600 mt-1">
              No Robot token is set yet, so there is nothing to sync. Set one on the Channels tab
              first.
            </p>
          )}
        </div>
        <Button
          type="button"
          variant="secondary"
          disabled={!canSync || syncMutation.isPending}
          isLoading={syncMutation.isPending}
          aria-label="Sync the Robot token to every bound Factory company now"
          onClick={() => {
            syncMutation.reset();
            syncMutation.mutate();
          }}
        >
          Sync now
        </Button>
      </div>

      <ul className="mt-3 divide-y divide-gray-100 border-t border-gray-100">
        {companies.map((row) => (
          <li
            key={row.paperclipCompanyId}
            className="flex items-center justify-between gap-4 py-2"
          >
            <span className="font-mono text-xs text-gray-600">{row.paperclipCompanyId}</span>
            <RobotRowBadge row={row} />
          </li>
        ))}
      </ul>

      {syncMutation.isError && (
        <p className="mt-2 text-sm text-red-600">{(syncMutation.error as Error).message}</p>
      )}

      {syncMutation.isSuccess && (
        <p className="mt-2 text-sm text-gray-600">
          Requested for {syncMutation.data.requested}{' '}
          {syncMutation.data.requested === 1 ? 'company' : 'companies'}. The result arrives from
          the Factory&trade; a moment later — press Refresh to see it.
        </p>
      )}
    </div>
  );
}
